/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import TopNavigation from './components/TopNavigation';
import Dashboard from './components/Dashboard';
import HousingCalculator from './components/HousingCalculator';
import RRSPCalculator from './components/RRSPCalculator';
import PersonalFinanceForecast from './components/PersonalFinanceForecast';
import PersonalForecast from './components/PersonalForecast';
import CarComparisonCalculator from './components/CarComparisonCalculator';
import CarCalculatorInfoPage from './components/CarCalculatorInfoPage';
import CareerComparisonCalculator from './components/CareerComparisonCalculator';
import CareerComparisonInfoPage from './components/CareerComparisonInfoPage';
import AccountingApp from './components/accounting/AccountingApp';
import AccountingInfoPage from './components/AccountingInfoPage';
import PayrollCalculator from './components/PayrollCalculator';
import TaxToolsApp from './components/TaxToolsApp';
import PersonalForecastInfoPage from './components/PersonalForecastInfoPage';
import HousingCalculatorInfoPage from './components/HousingCalculatorInfoPage';
import RetirementPlannerInfoPage from './components/RetirementPlannerInfoPage';
import USRetirementPlannerInfoPage from './components/USRetirementPlannerInfoPage';
import IncomeTaxCalculator from './components/IncomeTaxCalculator';
import IncomeTaxInfoPage from './components/IncomeTaxInfoPage';
import TaxToolsInfoPage from './components/TaxToolsInfoPage';
import USIncomeTaxInfoPage from './components/USIncomeTaxInfoPage';
import USRetirementPlanner from './components/USRetirementPlanner';
import EmailVerificationPage from './components/EmailVerificationPage';
import PasswordResetPage from './components/PasswordResetPage';
import AccountPage from './components/AccountPage';
import GoogleAuthCallback from './components/GoogleAuthCallback';
import WelcomeModal from './components/WelcomeModal';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import PricingPage from './components/PricingPage';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import TylerStateSummary from './components/TylerStateSummary';
import SchedulingDashboard from './components/scheduling/SchedulingDashboard';
import StaffDashboard from './components/scheduling/StaffDashboard';
import AcceptStaffInvite from './components/scheduling/AcceptStaffInvite';
import LarryLinePage from './components/larryline/LarryLinePage';
import LarryLineInfoPage from './components/LarryLineInfoPage';
import SchedulingInfoPage from './components/SchedulingInfoPage';
import PublicBookingPage from './components/scheduling/PublicBookingPage';
import { PublicPollPage } from './components/scheduling/MeetingPolls';
import { PublicGroupEventPage } from './components/scheduling/GroupEvents';
import { PublicRoutingFormPage } from './components/scheduling/RoutingForms';
import { useParams } from 'react-router-dom';

// Wrapper components for scheduling public pages
const PublicPollPageWrapper: React.FC = () => {
  const { pollUid } = useParams<{ pollUid: string }>();
  return <PublicPollPage pollUid={pollUid || ''} />;
};

const PublicGroupEventWrapper: React.FC = () => {
  const { subdomain, slug } = useParams<{ subdomain: string; slug: string }>();
  return <PublicGroupEventPage subdomain={subdomain || ''} slug={slug || ''} />;
};

const PublicRoutingFormWrapper: React.FC = () => {
  const { subdomain, slug } = useParams<{ subdomain: string; slug: string }>();
  return <PublicRoutingFormPage subdomain={subdomain || ''} slug={slug || ''} />;
};
import BlogIndex from './components/blog/BlogIndex';
import CanadianInfrastructureBlog from './components/blog/CanadianInfrastructureBlog';
import OntarioSpeedCamerasBlog from './components/blog/OntarioSpeedCamerasBlog';
import HowToWinWithAIBlog from './components/blog/HowToWinWithAIBlog';
import StructuredData from './components/StructuredData';
import PerformanceMonitor from './components/PerformanceMonitor';
import SEOHead from './components/SEOHead';
import guestStorageService from './services/guest-storage';
import authService from './services/auth-service';
import { User } from './types/auth';
import {TylerState} from "./utils/TylerState";
import { CountryProvider, useCountry } from './contexts/CountryContext';
import './styles/main.css';

// Dynamic SEO content based on country selection
const DynamicSEOContent: React.FC = () => {
  const { isUSA } = useCountry();

  if (isUSA) {
    return (
      <div style={{position: 'absolute', left: '-9999px', top: '-9999px'}} aria-hidden="true">
        <h2>American Financial Calculators - 401k, IRA, Retirement Planning</h2>
        <p>Free financial planning tools for Americans. Compare car lease vs buy options, rent vs buy house decisions, 401k vs Roth IRA investments, career paths, and retirement planning. Make informed financial decisions with our comprehensive calculators.</p>
        <h2>Car Lease vs Buy Calculator</h2>
        <p>Should you lease or buy a car in the US? Our calculator compares total costs including depreciation, interest rates, maintenance, and tax benefits across all 50 states.</p>
        <h2>Rent vs Buy House Calculator</h2>
        <p>Deciding whether to rent or buy a home in America? Compare mortgage payments, property taxes, PMI, maintenance costs, and investment opportunities to determine the best option.</p>
        <h2>401k and IRA Calculator</h2>
        <p>Compare Traditional 401k, Roth 401k, Traditional IRA, and Roth IRA to optimize your American retirement savings strategy with tax-efficient planning.</p>
        <h2>Career Comparison Calculator</h2>
        <p>Compare different career paths with US federal and state tax calculations, salary growth, benefits, and long-term financial outcomes.</p>
        <h2>US Retirement Planning Calculator</h2>
        <p>Plan your retirement with Monte Carlo simulation, Social Security optimization, and comprehensive tools for estimating retirement income and expenses in the United States.</p>
      </div>
    );
  }

  return (
    <div style={{position: 'absolute', left: '-9999px', top: '-9999px'}} aria-hidden="true">
      <h2>Canadian Financial Calculators - Lease vs Buy, RRSP, Career Comparison</h2>
      <p>Free financial planning tools for Canadians. Compare car lease vs buy options, rent vs buy house decisions, RRSP vs TFSA investments, career paths, and retirement planning. Make informed financial decisions with our comprehensive calculators.</p>
      <h2>Car Lease vs Buy Calculator</h2>
      <p>Should you lease or buy a car in Canada? Our calculator compares total costs including depreciation, interest rates, maintenance, and tax benefits to help you make the right choice.</p>
      <h2>Rent vs Buy House Calculator</h2>
      <p>Deciding whether to rent or buy a home? Compare mortgage payments, property taxes, maintenance costs, and investment opportunities to determine the best option for your situation.</p>
      <h2>RRSP vs TFSA Calculator</h2>
      <p>Compare registered retirement savings plans (RRSP) and tax-free savings accounts (TFSA) to optimize your Canadian retirement savings strategy.</p>
      <h2>Career Comparison Calculator</h2>
      <p>Compare different career paths including salary growth, benefits, work-life balance, and long-term financial outcomes.</p>
      <h2>Retirement Planning Calculator</h2>
      <p>Plan your retirement with comprehensive tools for estimating retirement income, expenses, and savings requirements in Canada.</p>
    </div>
  );
};

// Country-aware wrapper for Retirement Planner Info Page
const RetirementPlannerInfoWrapper: React.FC = () => {
  const { isUSA } = useCountry();
  return isUSA ? <USRetirementPlannerInfoPage /> : <RetirementPlannerInfoPage />;
};

// Country-aware wrapper for Income Tax Info Page
const IncomeTaxInfoWrapper: React.FC = () => {
  const { isUSA } = useCountry();
  return isUSA ? <USIncomeTaxInfoPage /> : <IncomeTaxInfoPage />;
};

const App: React.FC = () => {  
  const [user, setUser] = useState<User | null>(null);
  const [guestScenarios, setGuestScenarios] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [TylerState1, setTylerState1] = useState<Record<string, any>>({});

  //xState
    const buttonUpdateReact = (
   <span style={{ display: "none" }}>
    <button
     onClick={() => {
      setTylerState1(TylerState)
     }}
     id="updateReact"
     className="hidden"
    />{TylerState1._updated}
    </span>)

  // Load authentication state on app startup
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First, check for inconsistent state and clean it up
        const token = localStorage.getItem('auth_token');
        const userStr = localStorage.getItem('auth_user');

        if (userStr && !token) {
          console.log('Found user data without token, clearing inconsistent state');
          authService.clearInconsistentState();
        } else if (token && !userStr) {
          console.log('Found token without user data, clearing inconsistent state');
          authService.clearInconsistentState();
        }

        // Check if user is logged in and token is valid
        const isAuthenticated = await authService.checkAuthStatus();
        if (isAuthenticated) {
          // Refresh user data from backend to get latest tier/subscription info
          const currentUser = await authService.refreshUserData();
          if (currentUser && currentUser.id && currentUser.email) {
            // Convert auth service user to App user format
            const appUser: User = {
              id: currentUser.id,
              email: currentUser.email,
              username: currentUser.username,
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              is_active: currentUser.is_active,
              is_verified: currentUser.is_verified || false,
              is_admin: currentUser.is_admin || false,
              auth_method: currentUser.auth_method || 'email',
              google_id: currentUser.google_id,
              created_at: currentUser.created_at || new Date().toISOString(),
              // Legacy fields mapped from backend
              createdAt: currentUser.created_at || new Date().toISOString(),
              emailVerified: currentUser.is_verified || false,
              tier: currentUser.tier || 'free', // Use actual tier from backend
              subscriptionEndDate: (currentUser as any).subscription_end_date || undefined,
              subscription_status: (currentUser as any).subscription_status || 'inactive',
              googleId: currentUser.google_id,
              scenarios: []
            };
            console.log('🔄 Refreshed user data on startup:', { email: appUser.email, tier: appUser.tier });
            setUser(appUser);
          }
        }
      } catch (error) {
        console.log('Auth initialization warning (non-fatal):', error);
        // Do not logout on init errors (e.g., local CORS). Leave any stored auth intact.
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // React to auth updates without reloading the page
  useEffect(() => {
    const onAuthUpdated = () => {
      try {
        const stored = localStorage.getItem('auth_user');

        // Only process if we have user data (token is in httpOnly cookie)
        if (stored) {
          const currentUser = JSON.parse(stored);
          
          // Validate that we have essential user data
          if (currentUser.id && currentUser.email) {
            const appUser: User = {
              id: currentUser.id,
              email: currentUser.email,
              username: currentUser.username || currentUser.email?.split('@')[0] || 'user',
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              is_active: currentUser.is_active ?? true,
              is_verified: !!currentUser.is_verified,
              is_admin: currentUser.is_admin || false,
              auth_method: currentUser.auth_method || 'email',
              google_id: currentUser.google_id,
              created_at: currentUser.created_at || new Date().toISOString(),
              // Legacy fields
              createdAt: currentUser.created_at || new Date().toISOString(),
              emailVerified: !!currentUser.is_verified,
              tier: 'free',
              subscriptionEndDate: (currentUser as any).subscription_end_date || undefined,
              subscription_status: (currentUser as any).subscription_status || 'inactive',
              googleId: currentUser.google_id,
              scenarios: currentUser.scenarios || []
            };
            setUser(appUser);
          } else {
            console.log('Invalid user data in localStorage, clearing auth state');
            //authService.logout();
            setUser(null);
            setShowWelcomeModal(false);
          }
        } else {
          console.log('Missing token or user data in localStorage, clearing auth state');
          //authService.logout();
          setUser(null);
          setShowWelcomeModal(false);
        }
      } catch (e) {
        console.log('Failed to hydrate user from localStorage after auth:updated', e);
        //authService.logout();
        setUser(null);
        setShowWelcomeModal(false);
      }
    };

    window.addEventListener('auth:updated', onAuthUpdated);
    return () => window.removeEventListener('auth:updated', onAuthUpdated);
  }, []);

  // Load guest scenarios on app start
  useEffect(() => {
    const scenarios = guestStorageService.getAllGuestScenarios();
    setGuestScenarios(scenarios);
  }, []);

  // Listen for Google login success events
  useEffect(() => {
    const handleGoogleLoginSuccess = (event: CustomEvent) => {
      const detail: any = event.detail;
      const userPayload = detail && (detail.user || detail);
      if (userPayload) {
        handleLogin(userPayload);
      }
    };

    window.addEventListener('googleLoginSuccess', handleGoogleLoginSuccess as EventListener);
    
    return () => {
      window.removeEventListener('googleLoginSuccess', handleGoogleLoginSuccess as EventListener);
    };
  }, []);

  // Check auth status when route changes (for Google callback redirects)
  useEffect(() => {
    const checkAuthOnRouteChange = async () => {
      if (window.location.pathname === '/') {
        try {
          //window.console.log('🚀 FORCED LOG: checkAuthOnRouteChange called');
          const isAuthenticated = await authService.checkAuthStatus();
          //window.console.log('🚀 FORCED LOG: checkAuthStatus result:', isAuthenticated);
          //window.console.log('🚀 FORCED LOG: Current user state:', user);
          
          if (isAuthenticated && !user) {
            //window.console.log('🚀 FORCED LOG: User appears authenticated but no user state, checking...');
            const currentUser = await authService.getCurrentUser();
            //window.console.log('🚀 FORCED LOG: getCurrentUser result:', currentUser);
            
            if (currentUser && currentUser.id && currentUser.email) {
              //window.console.log('🚀 FORCED LOG: Valid user found, setting user state');
              const appUser: User = {
                id: currentUser.id,
                email: currentUser.email,
                username: currentUser.username,
                first_name: currentUser.first_name,
                last_name: currentUser.last_name,
                is_active: currentUser.is_active,
                is_verified: currentUser.is_verified || false,
                is_admin: currentUser.is_admin || false,
                auth_method: currentUser.auth_method || 'email',
                google_id: currentUser.google_id,
                created_at: currentUser.created_at || new Date().toISOString(),
                // Legacy fields mapped from backend
                createdAt: currentUser.created_at || new Date().toISOString(),
                emailVerified: currentUser.is_verified || false,
                tier: 'free',
                subscriptionEndDate: (currentUser as any).subscription_end_date || undefined,
                subscription_status: (currentUser as any).subscription_status || 'inactive',
                googleId: currentUser.google_id,
                scenarios: []
              };
              setUser(appUser);
            } else {
              //window.console.log('🚀 FORCED LOG: No valid user found, NOT showing welcome modal');
              // Don't show welcome modal if there's no real user
            }
          } else if (!isAuthenticated && user) {
            //window.console.log('🚀 FORCED LOG: User state exists but not authenticated, clearing user state');
            setUser(null);
            setShowWelcomeModal(false);
          } else {
            //window.console.log('🚀 FORCED LOG: User not authenticated or already has user state');
          }
        } catch (error) {
          window.console.error('🚀 FORCED LOG: Auth check on route change failed:', error);
        }
      }
    };

    checkAuthOnRouteChange();
  }, [user]);

  const handleLogin = (userData: any) => {
    // Determine user tier based on login response
    const tier = userData.tier || 'free';
    const newUser: User = {
      id: userData.id || Date.now(),
      email: userData.email,
      username: userData.username || userData.email?.split('@')[0] || 'user',
      first_name: userData.first_name,
      last_name: userData.last_name,
      is_active: userData.is_active ?? true,
      is_verified: userData.is_verified || false,
      is_admin: userData.is_admin || false,
      auth_method: userData.auth_method || 'email',
      google_id: userData.google_id,
      created_at: userData.created_at || new Date().toISOString(),
      // Legacy fields
      createdAt: userData.created_at || new Date().toISOString(),
      emailVerified: userData.is_verified || false,
      tier,
      subscriptionEndDate: userData.subscriptionEndDate,
      googleId: userData.googleId,
      scenarios: userData.scenarios || []
    };
    setUser(newUser);
    
    // Show welcome modal for new logins
    setShowWelcomeModal(true);
  };

  const handleLogout = () => {
    console.log('App: handleLogout called, clearing user state');
    // Clear auth service state
    authService.logout();
    // Clear app user state
    setUser(null);
    // Clear guest scenarios when user logs out
    guestStorageService.clearAllGuestScenarios();
    setGuestScenarios({});
    // Close welcome modal if open
    setShowWelcomeModal(false);
    console.log('App: Logout complete, user state cleared');
  };

  const saveGuestScenario = (calculatorType: string, data: any) => {
    guestStorageService.saveGuestScenario(calculatorType, data);
    setGuestScenarios(prev => ({
      ...prev,
      [calculatorType]: data
    }));
  };

  const getGuestScenario = (calculatorType: string) => {
    return guestStorageService.getGuestScenario(calculatorType);
  };

  // Show loading screen while initializing auth
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 50%, #a93226 100%)',
        color: 'white',
        fontSize: '1.2rem'
      }}>
        Loading Larry's Help...
      </div>
    );
  }
  return (
    <CountryProvider>
    <Router>
      {/* Dynamic SEO - Updates per route */}
      <SEOHead />

      {/* SEO Structured Data */}
      <StructuredData
        type="website"
        data={{
          name: "Larry's Help",
          description: "Free financial calculators for Canada and the US, free bookkeeping software, and a Calendly-style scheduling platform."
        }}
      />
      <StructuredData
        type="organization"
        data={{
          name: "Larry's Help",
          description: "Financial planning and retirement calculator platform"
        }}
      />

      {/* Performance Monitoring */}
      <PerformanceMonitor />

      <div className="App">
        <span style={{display:"none"}} >{buttonUpdateReact} </span>

        {/* SEO Content - Dynamic based on country */}
        <DynamicSEOContent />

        <main className="main-content">
          {/* Welcome Modal - Only show when there's a real authenticated user with email */}
          {user && user.id && user.email && (
            <WelcomeModal 
              isOpen={showWelcomeModal}
              onClose={() => setShowWelcomeModal(false)}
              userEmail={user.email}
            />
          )}
          
          <Routes>
          {TylerState.user ? (
            <Route 
              path="/" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <Dashboard 
                    user={TylerState.user}
                    guestScenarios={guestScenarios}
                  />
                </>
              } 
            />
            ) : (
            <Route 
              path="/" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} onLogout={handleLogout} />
                  <Dashboard 
                    guestScenarios={guestScenarios}
                  />
                </>
              } 
            />
            )}
            <Route 
              path="/housing" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <HousingCalculator 
                    user={TylerState.user}
                    guestScenario={getGuestScenario('housing')}
                    onSaveScenario={(data) => saveGuestScenario('housing', data)}
                  />
                </>
              } 
            />
            <Route 
              path="/rrsp" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <RRSPCalculator 
                    user={TylerState.user}
                    guestScenario={getGuestScenario('rrsp')}
                    onSaveScenario={(data) => saveGuestScenario('rrsp', data)}
                  />
                </>
              } 
            />
            <Route
              path="/forecast"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <PersonalForecast />
                </>
              }
            />
            <Route
              path="/tax-tools"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <TaxToolsApp />
                </>
              }
            />
            <Route
              path="/income-tax"
              element={<Navigate to="/tax-tools?tab=estimator" replace />}
            />
            <Route
              path="/income-tax-info"
              element={<Navigate to="/tax-tools-info" replace />}
            />
            <Route
              path="/tax-tools-info"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <TaxToolsInfoPage />
                </>
              }
            />
            <Route
              path="/car-comparison" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <CarComparisonCalculator 
                    user={TylerState.user}
                    guestScenario={getGuestScenario('car-comparison')}
                    onSaveScenario={(data: any) => saveGuestScenario('car-comparison', data)}
                  />
                </>
              } 
            />
            <Route 
              path="/car-calculator-info" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <CarCalculatorInfoPage />
                </>
              } 
            />
            <Route 
              path="/career-comparison" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <CareerComparisonCalculator 
                    user={TylerState.user}
                    guestScenario={getGuestScenario('career-comparison')}
                    onSaveScenario={(data: any) => saveGuestScenario('career-comparison', data)}
                  />
                </>
              } 
            />
            <Route 
              path="/career-comparison-info" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <CareerComparisonInfoPage />
                </>
              } 
            />
            <Route
              path="/housing-calculator-info"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <HousingCalculatorInfoPage />
                </>
              }
            />
            <Route
              path="/personal-forecast-info"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <PersonalForecastInfoPage />
                </>
              }
            />
            <Route
              path="/retirement-planner-info"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <RetirementPlannerInfoWrapper />
</>
              }
            />
            <Route
              path="/us-retirement"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <USRetirementPlanner />
                </>
              }
            />
            <Route
              path="/payroll"
              element={<Navigate to="/tax-tools?tab=payroll" replace />}
            />
            <Route
              path="/accounting-info"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <AccountingInfoPage />
                </>
              } 
            />
            <Route 
              path="/verify-email" 
              element={<EmailVerificationPage />} 
            />
            <Route
              path="/reset-password"
              element={<PasswordResetPage />}
            />
            <Route
              path="/privacy-policy"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <PrivacyPolicy />
                </>
              }
            />
            <Route
              path="/terms-of-service"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <TermsOfService />
                </>
              }
            />
            <Route
              path="/pricing"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <PricingPage />
                </>
              }
            />
            <Route
              path="/account" 
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <AccountPage 
                    user={TylerState.user}
                    onLogin={handleLogin}
                    onLogout={handleLogout}
                  />
                </>
              } 
            />
            <Route
              path="/auth/google/callback"
              element={<GoogleAuthCallback />}
            />
            <Route
              path="/analytics"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <AnalyticsDashboard />
                </>
              }
            />
            {/* Larry Line Info Page */}
            <Route
              path="/larry-line-info"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <LarryLineInfoPage />
                </>
              }
            />
            {/* Larry Line - Anti-Tanking Draft Tracker */}
            <Route
              path="/larry-line"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <LarryLinePage />
                </>
              }
            />
            <Route
              path="/larry-line/:league"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <LarryLinePage />
                </>
              }
            />
            {/* Scheduling Info Page */}
            <Route
              path="/scheduling-info"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <SchedulingInfoPage />
                </>
              }
            />
            {/* Scheduling Routes - cal.larryshelp.org */}
            <Route
              path="/scheduling"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <SchedulingDashboard />
                </>
              }
            />
            {/* Staff Dashboard */}
            <Route
              path="/scheduling/staff"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <StaffDashboard />
                </>
              }
            />
            {/* Accept Staff Invite */}
            <Route
              path="/scheduling/accept-invite"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <AcceptStaffInvite />
                </>
              }
            />
            <Route
              path="/scheduling/*"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <SchedulingDashboard />
                </>
              }
            />
            <Route
              path="/book/:subdomain"
              element={<PublicBookingPage />}
            />
            <Route
              path="/book/:subdomain/:eventSlug"
              element={<PublicBookingPage />}
            />
            {/* Public Poll Page */}
            <Route
              path="/poll/:pollUid"
              element={<PublicPollPageWrapper />}
            />
            {/* Public Group Event Registration */}
            <Route
              path="/group/:subdomain/:slug"
              element={<PublicGroupEventWrapper />}
            />
            {/* Public Routing Form */}
            <Route
              path="/route/:subdomain/:slug"
              element={<PublicRoutingFormWrapper />}
            />
            <Route
              path="/accounting"
              element={
                <>
                  <TopNavigation onLogin={handleLogin} user={TylerState.user} onLogout={handleLogout} />
                  <AccountingApp user={TylerState.user} />
                </>
              }
            />
            <Route
              path="/blog"
              element={<BlogIndex />}
            />
            <Route
              path="/blog/canadian-infrastructure"
              element={<CanadianInfrastructureBlog />}
            />
            <Route
              path="/blog/ontario-speed-cameras"
              element={<OntarioSpeedCamerasBlog />}
            />
            <Route
              path="/blog/how-to-win-with-ai"
              element={<HowToWinWithAIBlog />}
            />
            <Route
              path="/tylerstate-summary"
              element={<TylerStateSummary />}
            />
          </Routes>
        </main>
      </div>
    </Router>
    </CountryProvider>
  );
};

export default App;
